import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Grid } from "@mui/material";

import BasicDialog from "../../base/BasicDialog";
import LoadingButton from "@mui/lab/LoadingButton";

import LoaderWrapper from "../../wrapper/LoaderWrapper/LoaderWrapper";
import AutocompleteField from "../../field/AutocompleteField";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";

import useAlertService from "../../../services/alertService";
import useEquipmentService from "../../../services/equipmentService";

import { useForm } from "../../../hooks/useForm";

import { convertObjectListFromBackendToTranslatedOptionObject } from "../../../helpers/methods";

const DeleteAlertDialog = (props) => {
  const { t } = useTranslation();

  const getDialogTitle = () => {
    return t(`dialog.delete_alert.delete_alerts`);
  };

  const snackbarAlert = useSnackbarAlert();

  const { formValue, onChangeAutocompleteFieldWithObjectOptions } = useForm({});

  const { getAlertSelectList } = useAlertService();
  const { deleteEquipmentBmsAlert } = useEquipmentService();

  const alertSelectList = useAsync(
    () => getAlertSelectList({ alert_group: props.itemType }),
    [props.itemType]
  );

  const deleteEquipmentBmsAlertFn = useAsyncFn(deleteEquipmentBmsAlert);
  const deleteFn =
    props.itemType === "equipment_bms" ? deleteEquipmentBmsAlertFn : () => {};

  const handleSubmit = () => {
    deleteFn
      .execute(props.itemIds, formValue.alert)
      .then((data) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.alerts_deleted")
        );
        if (props.onSubmit) {
          props.onSubmit(true);
        }

        if (props.refreshAlertCount) {
          props.refreshAlertCount();
        }

        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          t("snackbar_alert.occurred_error_during_saving_changes")
        );
      });
  };

  const isFormValid = formValue.alert;

  if (alertSelectList.loading) return <LoaderWrapper showLoader={true} />;

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      title={getDialogTitle()}
      maxWidth="sm"
      showDialogActions
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        rowGap={1}
        columnSpacing={2}
      >
        <Grid item xs={12}>
          <AutocompleteField
            name="alert"
            label={t("dialog.delete_alert.alerts")}
            value={formValue.alert}
            options={convertObjectListFromBackendToTranslatedOptionObject(
              alertSelectList.value,
              "alert_type"
            )}
            optionLabelKey={"alert_type"}
            isObjectOption={true}
            addNewValue={false}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={true}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LoadingButton
            disabled={!isFormValid}
            loading={deleteFn.loading}
            type={"submit"}
            fullWidth
            onClick={handleSubmit}
            variant="contained"
          >
            {t("confirm")}
          </LoadingButton>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LoadingButton
            fullWidth
            onClick={props.onClose}
            disabled={deleteFn.loading}
            variant="contained"
            color="secondary"
          >
            {t("cancel")}
          </LoadingButton>
        </Grid>
      </Grid>
    </BasicDialog>
  );
};

DeleteAlertDialog.propTypes = {
  itemType: PropTypes.oneOf(["equipment_bms"]),
  itemIds: PropTypes.arrayOf(PropTypes.string),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  refreshAlertCount: PropTypes.func,
};

DeleteAlertDialog.defaultProps = {
  itemType: "equipment_bms",
  open: false,
};

export default DeleteAlertDialog;
